import "./styles/RecentlyUsedSection.scss";

import { Col, GridContainer, GridItem } from "@amzn/stencil-react-components/layout";
import { PaginationVariant } from "@amzn/stencil-react-components/pagination";
import { Text } from "@amzn/stencil-react-components/text";
import { Pagination } from "@amzn/uxm-stencil-react-components/lib/src/stencil";
import { AiForMeItem } from "common/components/AIForMe/AiForMeItem";
import { useCompare } from "common/context/compare";
import { TrackClickInteraction } from "common/helpers";
import { VoteHandler } from "common/hooks";
import { getString } from "common/uistringlabels/uiStringUtils";
import { AIForMeTool, SORTING_PRIORITY } from "lib/models/aiforme-models";
import { CatalogicalService } from "lib/services";
import { FeedbackModelRecord } from "lib/services/catalogical-models";
import React, { FC, useEffect, useState } from "react";

/**
 * Extended AI tool model with last used date information
 */
export interface RecentlyUsedTool extends AIForMeTool {
  lastUsedDate: string;
}

/** Path to the cat illustration shown when card slots are empty. */
const CAT_ILLUSTRATION_PATH = "/img/Illustration-Cat.svg";

/** Number of tools displayed per page. */
const TOOLS_PER_PAGE = 3;

/** Maximum number of tools across all pages. */
const MAX_TOOLS = 9;

const FIRST_PAGE = 1;

/**
 * Props for the RecentlyUsedSection component.
 */
export interface RecentlyUsedSectionProps {
  tools: RecentlyUsedTool[];
  voteHandler: VoteHandler;
  client: CatalogicalService<AIForMeTool>;
  /** User's previous votes/ratings keyed by taxonomy ID. */
  userFeedbacks: Record<string, FeedbackModelRecord>;
  /** Callback to track click interactions when user clicks "Learn More". */
  onTrackClick: TrackClickInteraction;
  /** Callback fired after a visit is successfully recorded. */
  onVisitRecorded?: (tool: AIForMeTool, visitedAt: string) => void;
}

/**
 * Section component displaying recently used AI tools using the shared AiForMeItem card.
 * Shows 3 tools per page with pagination. Empty card slots show a centered cat illustration.
 */
export const RecentlyUsedSection: FC<RecentlyUsedSectionProps> = ({
  tools,
  voteHandler,
  client,
  userFeedbacks,
  onTrackClick,
  onVisitRecorded,
}) => {
  const { addTool, removeTool, selectedToolIds, canAddMore, setReviewFlyoutOpen } = useCompare();

  const [currentPage, setCurrentPage] = useState<number>(FIRST_PAGE);
  const cappedTools = tools.slice(0, MAX_TOOLS);

  const totalPages = Math.max(1, Math.ceil(cappedTools.length / TOOLS_PER_PAGE));
  const effectivePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const startIndex = (effectivePage - 1) * TOOLS_PER_PAGE;
  const pageTools = cappedTools.slice(startIndex, startIndex + TOOLS_PER_PAGE);
  const emptySlots = TOOLS_PER_PAGE - pageTools.length;

  return (
    <Col rowGap={24} alignItems="center" data-testid="recently-used-section">
      <Text fontSize="T400" fontWeight="bold" data-testid="recently-used-heading">
        {getString("aiForMe.landingPage.recentlyUsedToolsSection.sectionTitle")}
      </Text>

      <GridContainer>
        {pageTools.map((tool, index) => {
          const isCompareSelected = selectedToolIds.has(tool.taxonomyId);
          return (
            <AiForMeItem
              key={tool.taxonomyId}
              item={tool}
              index={startIndex + index}
              voteHandler={voteHandler}
              client={client}
              gridSpan={{ lg: 4, md: 4 }}
              isCompareSelected={isCompareSelected}
              isCompareDisabled={!canAddMore && !isCompareSelected}
              onCompareAdd={addTool}
              onCompareRemove={removeTool}
              onTrackClick={onTrackClick}
              onReviewFlyoutToggle={setReviewFlyoutOpen}
              userVote={userFeedbacks[tool.taxonomyId]}
              lastUsedDate={tool.lastUsedDate}
              hideTags
              hideCompare
              onVisitRecorded={onVisitRecorded}
              sortingPriorityTag={SORTING_PRIORITY.find((sp) => sp.value === tool.sortingPriority)?.label}
            />
          );
        })}
        {emptySlots > 0 && (
          <GridItem
            lg={Math.min(emptySlots * 4, 12) as 4 | 8 | 12}
            md={Math.min(emptySlots * 4, 8) as 4 | 8}
            data-testid="empty-state-placeholder"
          >
            <div className="recently-used-empty-state">
              <img src={CAT_ILLUSTRATION_PATH} alt="No recently visited tools" width={247} height={200} />
              <Text
                fontSize="T200"
                color="var(--st-dt-color-text-secondary)"
                textAlign="center"
                margin={{ top: "0.5rem" }}
              >
                {getString("aiForMe.landingPage.recentlyUsedToolsSection.emptyStateMessage")}
              </Text>
            </div>
          </GridItem>
        )}
      </GridContainer>

      {totalPages > 1 && (
        <Pagination
          numberOfPages={totalPages}
          selectedPage={effectivePage}
          onPageSelect={setCurrentPage}
          variant={PaginationVariant.Dotted}
          touchpoint="recently-used-pagination"
        />
      )}
    </Col>
  );
};
